import { useLocalization } from '@lib/localization';
import {
  Box, Button, Checkbox, LinearProgress, Paper, Skeleton, Table, TableBody, TableCell, TableHead, TablePagination,
  TableRow, TableSortLabel, Typography
} from '@mui/material';
import { type ReactTable, type RowData } from '@tanstack/react-table';
import { type ReactNode, useEffect, useRef } from 'react';
import {
  cardStyles, flexibleColumnWidth, messageCellStyles, paginationStyles, progressStyles, scrollStyles, selectColumnWidth,
  tableStyles
} from './DataTable.styles';
import { type DataTableColumnMeta, type DataTableFeatures, defaultPageSize, pageSizes } from './useDataTable';

/** The class names that apply, as one attribute. */
const classes = (...names: (string | false | undefined)[]) => names.filter(Boolean).join(' ');

/**
 * Marks where columns are out of view: `dt-scrolled` while some are scrolled under the row's
 * name, `dt-more` while some lie past the right edge. Set on the element and not through state:
 * a scroll fires many times a second, and the lines it shows are no reason to draw the table again.
 */
const markEdges = (scroller: HTMLElement) => {
  scroller.classList.toggle('dt-scrolled', scroller.scrollLeft > 0);
  // Within a pixel the columns fit: the browser rounds what it measures.
  scroller.classList.toggle('dt-more', scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1);
};

interface DataTableProps<TData extends RowData> {
  table: ReactTable<DataTableFeatures, TData>;
  /** Names the table to a screen reader. */
  label: string;
  /** Stands over the table, inside its card: the segments, the tools, the bar for what is picked. */
  toolbar?: ReactNode;
  /** How a row is named in the label of its checkbox. */
  rowName: (row: TData) => string;
  /** Nothing has been read yet: placeholders stand in for the rows. */
  isLoading: boolean;
  /** A read is on its way over rows already shown: a thin bar over them, and they stay. */
  isFetching: boolean;
  /** The last read failed: a first one, another page, or the same one read again after a change. */
  isError: boolean;
  onRetry: () => void;
  /** A row drawn quieter: there, but out of use, as a suspended account is. Its marks keep their colours. */
  isMuted?: (row: TData) => boolean;
  /** What is said where there are no rows to show. */
  empty: ReactNode;
}

/**
 * A table of the app, drawn from a table `useDataTable` made: the column names that sort, the
 * rows, and the pages under them, in a card as tall as its rows. Where the columns are wider than
 * the card the rows scroll sideways, and the column that names a row and the actions stay in
 * view. When the table was given rows to pick (`canSelect`), each of those has a checkbox before it.
 */
const DataTable = <TData extends RowData>({
  table, label, toolbar, rowName, isLoading, isFetching, isError, onRetry, isMuted, empty,
}: DataTableProps<TData>) => {
  const { t } = useLocalization();
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = scroller.current;

    if (!element) return undefined;

    markEdges(element);

    if (typeof ResizeObserver === 'undefined') return undefined;

    // The card narrows with the window and the menu, and the table widens with a column: either
    // hides columns past the right edge, or brings them back, without a scroll.
    const resized = new ResizeObserver(() => markEdges(element));

    resized.observe(element);
    if (element.firstElementChild) resized.observe(element.firstElementChild);

    return () => resized.disconnect();
  }, []);

  const rows = table.getRowModel().rows;
  const selectable = table.options.enableRowSelection !== false;
  const columnCount = table.getAllLeafColumns().length + (selectable ? 1 : 0);
  const stickyLeft = selectable ? selectColumnWidth : 0;
  // What the columns take together: below it the table scrolls sideways rather than squeeze them.
  const minWidth = stickyLeft + table.getAllLeafColumns()
    .reduce((sum, column) => sum + (column.columnDef.meta?.width ?? flexibleColumnWidth), 0);
  const pageCanSelect = rows.some((row) => row.getCanSelect());
  // The pages are offered while there is more than one, or while the reader has picked a page
  // size of their own, so they can pick it back; rows that fit on one default page need none.
  const { pageSize } = table.state.pagination;
  const showsPages = table.getRowCount() > pageSize || pageSize !== defaultPageSize;

  /** A header cell and a body cell of one column are placed alike. */
  const cellProps = (meta: DataTableColumnMeta | undefined) => ({
    className: classes(
      meta?.sticky === 'start' && 'dt-sticky-start',
      meta?.sticky === 'end' && 'dt-sticky-end',
      meta?.align === 'end' && 'dt-end',
    ),
    style: { width: meta?.width, left: meta?.sticky === 'start' ? stickyLeft : undefined },
  });

  const message = (content: ReactNode) => (
    <TableRow>
      <TableCell colSpan={columnCount}>
        <Box sx={messageCellStyles}>{content}</Box>
      </TableCell>
    </TableRow>
  );

  const body = () => {
    if (isLoading) {
      return Array.from({ length: 8 }, (_, index) => (
        <TableRow key={index}>
          {Array.from({ length: columnCount }, (_, cell) => (
            <TableCell key={cell}><Skeleton variant="text" width={cell === 0 ? '60%' : '40%'} /></TableCell>
          ))}
        </TableRow>
      ));
    }

    if (isError) {
      return message(
        <>
          <Typography variant="body2">{t('DataTable_LoadFailed')}</Typography>
          <Button size="small" variant="outlined" onClick={onRetry} sx={{ textTransform: 'none' }}>{t('DataTable_Retry')}</Button>
        </>
      );
    }

    if (rows.length === 0) return message(empty);

    return rows.map((row) => {
      // A row that can no longer be picked (taken off single sign-on, say) keeps its id in what
      // is picked; it is drawn as not picked, as the bulk actions count it.
      const isSelected = row.getIsSelected() && row.getCanSelect();

      return (
        <TableRow
          key={row.id}
          className={classes('dt-row', isSelected && 'dt-selected', isMuted?.(row.original) && 'dt-muted')}
        >
          {selectable && (
            // A row that cannot be picked has no checkbox at all rather than one that is switched off.
            <TableCell className="dt-select dt-sticky-start" style={{ left: 0 }}>
              {row.getCanSelect() && (
                <Checkbox
                  size="small"
                  checked={isSelected}
                  onChange={row.getToggleSelectedHandler()}
                  slotProps={{ input: { 'aria-label': t('DataTable_SelectRow', [rowName(row.original)]) } }}
                />
              )}
            </TableCell>
          )}
          {row.getAllCells().map((cell) => (
            <TableCell key={cell.id} {...cellProps(cell.column.columnDef.meta)}>
              <div className="dt-cell"><table.FlexRender cell={cell} /></div>
            </TableCell>
          ))}
        </TableRow>
      );
    });
  };

  return (
    <Paper variant="outlined" sx={cardStyles}>
      {toolbar}
      <Box sx={{ position: 'relative' }}>
        {isFetching && !isLoading && <LinearProgress sx={progressStyles} />}
        <Box ref={scroller} sx={scrollStyles} onScroll={(event) => markEdges(event.currentTarget)}>
          <Table aria-label={label} sx={tableStyles} style={{ minWidth }}>
            <TableHead>
              {table.getHeaderGroups().map((group) => (
                <TableRow key={group.id}>
                  {selectable && (
                    <TableCell className="dt-select dt-sticky-start" style={{ left: 0 }}>
                      <Checkbox
                        size="small"
                        checked={table.getIsAllPageRowsSelected()}
                        // In this version "some" is true when all are too.
                        indeterminate={table.getIsSomePageRowsSelected() && !table.getIsAllPageRowsSelected()}
                        disabled={!pageCanSelect}
                        onChange={table.getToggleAllPageRowsSelectedHandler()}
                        slotProps={{ input: { 'aria-label': t('DataTable_SelectPage') } }}
                      />
                    </TableCell>
                  )}
                  {group.headers.map((header) => {
                    const { column } = header;
                    const sorted = column.getIsSorted();

                    return (
                      <TableCell
                        key={header.id}
                        sortDirection={sorted || false}
                        {...cellProps(column.columnDef.meta)}
                      >
                        {column.getCanSort() ? (
                          // While a column does not sort, its arrow shows which way a click would.
                          <TableSortLabel
                            active={Boolean(sorted)}
                            direction={sorted || column.getNextSortingOrder() || 'asc'}
                            onClick={column.getToggleSortingHandler()}
                          >
                            <table.FlexRender header={header} />
                          </TableSortLabel>
                        ) : (
                          <table.FlexRender header={header} />
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>{body()}</TableBody>
          </Table>
        </Box>
      </Box>
      {/* `DataGrid_*` are the keys of the MUI grid this table replaced, kept because every
          language already has them. */}
      {showsPages && <TablePagination
        component="div"
        count={table.getRowCount()}
        page={table.state.pagination.pageIndex}
        rowsPerPage={table.state.pagination.pageSize}
        rowsPerPageOptions={pageSizes}
        onPageChange={(_event, page) => table.setPageIndex(page)}
        onRowsPerPageChange={(event) => table.setPageSize(Number(event.target.value))}
        labelRowsPerPage={t('DataGrid_RowsPerPage')}
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
        getItemAriaLabel={(type) => t(type === 'next' ? 'DataTable_NextPage' : 'DataTable_PreviousPage')}
        sx={paginationStyles}
      />}
    </Paper>
  );
};

export default DataTable;
